import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinRoom = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [roomCode, setRoomCode] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const codeFromUrl = searchParams.get("room");
    if (codeFromUrl) {
      setRoomCode(codeFromUrl.toUpperCase());
    }
  }, [searchParams]);

  const handleJoinRoom = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!roomCode.trim() || !participantName.trim()) {
      setError("Please enter both room code and your name");
      return;
    }

    setLoading(true);
    try {
      // Check if room exists
      const response = await axios.get(`${API}/rooms/${roomCode.toUpperCase()}/status`);
      
      if (response.data.exists) {
        navigate(`/room/${encodeURIComponent(roomCode.trim().toUpperCase())}?name=${encodeURIComponent(participantName)}`);
      } else {
        setError("Room not found");
      }
    } catch (err) {
      setError("Failed to join room");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden text-white font-secondary">
      {/* Background */}
      <div 
        className="fixed inset-0 z-[-1]" 
        style={{ 
          backgroundImage: `url('/bg-hero.png')`, 
          backgroundSize: 'cover', 
          backgroundPosition: 'center' 
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8 short:py-4 short:justify-start short:pt-14">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="absolute top-6 short:top-3 left-6 short:left-3 text-[#00F0FF] hover:text-white transition-colors flex items-center gap-2"
          data-testid="back-button"
        >
          <svg className="w-6 h-6 short:w-5 short:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-lg short:text-sm">Back</span>
        </button>

        {/* Title */}
        <h1 
          className="text-5xl sm:text-7xl lg:text-8xl short:text-3xl short:sm:text-4xl font-primary text-center mb-4 short:mb-2 tracking-tight"
          style={{ fontFamily: 'Anton, sans-serif' }}
        >
          <span className="text-[#00F0FF]">JOIN</span>{" "}
          <span className="text-[#FFEA00]">ROOM</span>
        </h1>
        
        <p className="text-lg sm:text-xl short:text-xs text-[#A1A1AA] text-center mb-12 short:mb-4 max-w-md">
          Enter the room code to join the game
        </p>

        {/* Join Room Card */}
        <div className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-3xl p-6 sm:p-8 short:p-4 shadow-2xl relative z-10 w-full max-w-md mx-auto">
          <form onSubmit={handleJoinRoom} className="space-y-4">
            <div>
              <input
                type="text"
                placeholder="ROOM NAME"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-4 short:py-2 text-center text-2xl short:text-xl font-secondary tracking-wide focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-all"
                maxLength={100}
                data-testid="join-room-code-input"
              />
            </div>

            <div>
              <input
                type="text"
                placeholder="YOUR NAME"
                value={participantName}
                onChange={(e) => setParticipantName(e.target.value)}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-4 short:py-2 text-center text-xl short:text-lg font-secondary focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-all"
                maxLength={50}
                data-testid="join-room-name-input"
              />
            </div>

            {error && (
              <div className="text-[#FF0055] text-center text-sm" data-testid="join-room-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 short:py-2.5 mt-6 short:mt-3 bg-[#00F0FF] text-black font-primary text-2xl short:text-xl tracking-wide rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:scale-[1.02] active:scale-95 transition-all uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Anton, sans-serif' }}
              data-testid="join-room-button"
            >
              {loading ? "JOINING..." : "JOIN ROOM"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JoinRoom;
